import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, X, ChevronRight } from "lucide-react";

const STORAGE_KEY = "onenext.cookie-consent.v1";
const HIDE_ON_PREFIXES = ["/sign-in", "/sign-up", "/onboarding", "/app", "/admin"];
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

type Choice = "accepted-all" | "essential-only" | "custom";

type Categories = {
  essential: boolean;
  preferences: boolean;
};

type Consent = {
  choice: Choice;
  categories: Categories;
  decidedAt: string;
};

function readConsent(): Consent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Consent>;
    if (
      (parsed.choice === "accepted-all" ||
        parsed.choice === "essential-only" ||
        parsed.choice === "custom") &&
      typeof parsed.decidedAt === "string"
    ) {
      return parsed as Consent;
    }
    return null;
  } catch {
    return null;
  }
}

function writeConsent(c: Consent) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  } catch {
    // ignore storage errors
  }
}

async function logConsentToServer(c: Consent, path: string) {
  try {
    await fetch(`${basePath}/api/consent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        choice: c.choice,
        categories: c.categories,
        path,
      }),
    });
  } catch {
    // best-effort: client-side consent already stored, server log is bonus
  }
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [location] = useLocation();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [categories, setCategories] = useState<Categories>({
    essential: true,
    preferences: true,
  });

  useEffect(() => {
    if (HIDE_ON_PREFIXES.some((p) => location === p || location.startsWith(p + "/"))) {
      setVisible(false);
      return;
    }
    const existing = readConsent();
    setVisible(!existing);
    setCustomizing(false);
  }, [location]);

  if (!visible) return null;

  const decide = (choice: Choice, cats: Categories) => {
    const consent: Consent = {
      choice,
      categories: cats,
      decidedAt: new Date().toISOString(),
    };
    writeConsent(consent);
    void logConsentToServer(consent, location);
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-3 bottom-3 z-50 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md"
      role="region"
      aria-label={t("cookie.banner_aria")}
      data-testid="cookie-banner"
    >
      <div className="rounded-2xl border bg-background shadow-xl p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Cookie className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-semibold text-foreground">{t("cookie.title")}</p>
            <p className="mt-1 text-muted-foreground leading-relaxed">
              {t("cookie.description")}{" "}
              <Link
                href="/legal/cookies"
                className="text-primary hover:underline"
              >
                {t("cookie.learn_more")}
              </Link>
              .
            </p>

            {customizing && (
              <div
                className="mt-3 space-y-3 rounded-xl border p-3 bg-card/50"
                data-testid="cookie-categories"
              >
                <CategoryRow
                  title={t("cookie.auth_title")}
                  description={t("cookie.auth_desc")}
                  checked
                  disabled
                />
                <CategoryRow
                  title={t("cookie.prefs_title")}
                  description={t("cookie.prefs_desc")}
                  checked={categories.preferences}
                  onChange={(v) =>
                    setCategories((c) => ({ ...c, preferences: v }))
                  }
                  testid="cookie-pref-toggle"
                />
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {!customizing ? (
                <>
                  <Button
                    size="sm"
                    onClick={() =>
                      decide("accepted-all", { essential: true, preferences: true })
                    }
                    data-testid="cookie-accept"
                  >
                    {t("cookie.accept_all")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      decide("essential-only", { essential: true, preferences: false })
                    }
                    data-testid="cookie-essential"
                  >
                    {t("cookie.essential_only")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCustomizing(true)}
                    data-testid="cookie-customize"
                  >
                    {t("cookie.customize")}
                    <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={() => decide("custom", categories)}
                    data-testid="cookie-save-custom"
                  >
                    {t("cookie.save_choices")}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setCustomizing(false)}
                  >
                    {t("cookie.cancel")}
                  </Button>
                </>
              )}
            </div>
          </div>
          <button
            onClick={() =>
              decide("essential-only", { essential: true, preferences: false })
            }
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={t("cookie.close_aria")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
  testid,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  testid?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        data-testid={testid}
      />
    </div>
  );
}
